import {
  BadgeCheck, BarChart3, Building, Building2, CalendarRange, ClipboardList,
  Coins, FileText, FlaskConical, FolderKanban, Gauge, HardHat, Inbox, LayoutDashboard, LifeBuoy,
  MoreHorizontal, Megaphone, Radar, RefreshCw, Recycle, ScrollText,
  Settings, ShieldCheck, ShoppingCart, Send, Sun, Ticket, Timer,
  TriangleAlert, Truck, Users, Users2, Vault,
  type LucideIcon,
} from 'lucide-react';
import { isRouteItem, navItemVisible, type NavItem } from './navItems';

/**
 * THE route-to-workspace configuration (2026-08-31).
 *
 * Michael asked for "a route-to-workspace configuration instead of hardcoding
 * menu entries throughout the UI". This file is that configuration, and it is
 * the only place that knows which pages exist or where they belong: the
 * desktop sidebar, the mobile workspace sheet, /menu and the ⌘K palette all
 * read it. A page cannot appear on one surface and be missing from
 * another, and adding a page is one line here.
 *
 * Five workspaces plus a Tools footer (2026-09-08, Michael: "fewer, plainer
 * groups"), named for the WORKING DAY rather than the codebase — someone in
 * the yard lives in FIELD, someone at a desk lives in OFFICE. Where a page
 * could sit in two the tie is broken by who opens it most: Vendors is filed
 * under Office because purchasing opens it, not PMs.
 *
 * REORGANISED 2026-08-31 to the menu Michael wrote out, after the first cut of
 * the 31 Aug rail came back "confusing": he sent his own seven groups, and the reason
 * they had not been built was that eight of his entries had no page behind
 * them. Four are pages now (My Action Center, Daily Logs, Certifications,
 * Reports), two are wiring (My Tickets → the portal, Help → the Guide), and
 * two — Scheduling and Recent Activity — he dropped himself once told that
 * /schedule already IS the Field Board and no global activity feed exists.
 * Do not re-add them.
 *
 * Every entry below is reachable: a route that exists in App.tsx, an external
 * URL marked `external`, or an `action`. A menu that offers a dead link is
 * worse than a menu that is one item shorter.
 *
 * Four features Michael's list does not name — What's New, Equipment Inbox,
 * Job Blast, Sync — are filed at the bottom of More. Leaving them out of the
 * configuration would not shrink the app, it would only make them unreachable.
 *
 * `description` (2026-08-31, on NavItem in navItems.ts) exists for exactly one
 * consumer: /menu, the phone's More page, which is a scannable list of cards
 * rather than icons and has room for a line of plain-language explanation.
 * The desktop sidebar and the ⌘K palette ignore it on purpose —
 * do not wire it up there.
 */
export interface NavWorkspace {
  /** Stable key: collapsed/selected state is stored against it. */
  key: string;
  label: string;
  /**
   * What the flyout and /menu call the group, when the strip's short label would
   * read badly as a heading. Defaults to `label` — see workspaceHeading().
   * The strip tooltip and the section's aria-label stay `label` either way, so
   * "which icon is this" has one answer everywhere.
   */
  headingLabel?: string;
  icon: LucideIcon;
  items: NavItem[];
  /**
   * Where the sidebar's single row for this workspace goes (2026-09-25: the
   * sidebar is seven destinations, one row each — the pages inside are the
   * area's tab strip). Defaults to the first route item the person can open.
   */
  home?: string;
  /**
   * Where the desktop sidebar (2026-09-08) puts the group: a headed SECTION in
   * the body (default), or the compact FOOTER strip under the sections —
   * Michael's "utilities move out of the list". The phone sheet and /menu
   * ignore it and list every workspace the same way.
   */
  placement?: 'main' | 'footer';
  /**
   * Routes that BELONG to this workspace but carry no menu entry of their own
   * — a redirect, a printed-sticker short URL, a page reached only from
   * another page. Listing them here is what keeps the strip from going blank
   * when someone opens one; nothing is rendered from them.
   */
  extraRoutes?: string[];
}

export const NAV_WORKSPACES: NavWorkspace[] = [
  /*
   * SEVEN DESTINATIONS (2026-09-25). Michael: "I'd cut this from 29 sidebar
   * links to 7 main destinations. The sidebar should answer 'Where am I
   * working?' Each page can hold the related tools." The sidebar renders ONE
   * row per workspace below; the pages inside an area are its tab strip
   * (AreaTabs), an `action` item is a button on that strip, and the Tools
   * footer is unchanged. Gone from the menu on his list: Clients (a tab
   * inside Projects), Vendors (a tab inside Purchasing), Forms and Daily Logs
   * (inside each project — the standalone pages stay routable, and stay
   * Field's in extraRoutes so the sidebar still answers where you are).
   * NADC.AI left the menu for the assistant button (AssistantButton.tsx).
   */
  {
    key: 'today',
    label: 'Today',
    icon: Sun,
    home: '/today',
    // Today has its own address (audit 2026-10-14): "/" honours the "Where
    // DemoOps opens" setting, so a Today row pointing at "/" sent people whose
    // start page is Projects or the dashboard there instead — Today was out of
    // reach for them. "/" still renders Today for everyone else; /action-center
    // redirects here. /menu is the phone's More.
    extraRoutes: ['/', '/action-center', '/menu'],
    items: [
      { to: '/today', icon: Sun, label: 'Today', keywords: 'today home start needs you action center approvals alerts tasks queue todo on site now', description: 'What needs you, then what is happening on site today' },
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    items: [
      { to: '/projects', icon: FolderKanban, label: 'Projects', tabLabel: 'Projects', permission: 'projects.view', keywords: 'job site work', description: 'Every job — status, crew, and progress' },
      { to: '/bids', icon: ClipboardList, label: 'Bids', permission: 'bids.view', keywords: 'bid log proposal estimate award', description: 'Bid log — proposals, estimates, and awards' },
      // "Access client information through projects" — a tab here, not a menu row.
      { to: '/clients', icon: Building2, label: 'Clients', permission: 'clients.view', keywords: 'customer owner', description: 'Client companies and their contacts' },
      // "with Job Blast available as an action": a button on the strip.
      { to: '/job-blast', icon: Send, label: 'Job Blast', placement: 'action', permission: 'projects.job_blast', keywords: 'weekly email blast miranda jobs sheet', description: "Miranda's weekly job sheet email — preview and send" },
    ],
  },
  /*
   * DOCUVAULT (2026-10-09) — the eighth destination, on purpose. Michael:
   * "another area sorted by projects ... click into an area in the left menu
   * called Docustore or Docuvault. Then choose a project and have everything
   * at your fingertips." His answers to the design's question 1: right under
   * Projects, named Docuvault, with "docustore" as a search word. One page
   * (the job picker; a job's folders live under it), so no tab strip.
   *
   * vault.view, never projects.view: until go-live only the admin roles and
   * the trial people hold it (GET /api/v1/vault/config answers 403 for anyone
   * else), and at go-live everyone with project access gets it by migration —
   * no edit here either way. Operators never see it: they navigate
   * OPERATOR_WORKSPACES. Ctrl+K finds the area, never individual files (design
   * §3.7: no new search system in the palette).
   */
  {
    key: 'docuvault',
    label: 'Docuvault',
    icon: Vault,
    items: [
      {
        to: '/docuvault', icon: Vault, label: 'Docuvault', permission: 'vault.view',
        keywords: 'docustore documents files folders contracts change orders work orders permits drawings submittals photos pictures j drive job files shared drive',
        description: 'Every job’s documents — contracts, change orders, permits, drawings and photos',
      },
    ],
  },
  {
    key: 'field',
    label: 'Field',
    icon: HardHat,
    // /e/<code> is the sticker short URL; /time/* the entry detail and sheets;
    // /daily-logs and /forms are reached from inside a project now.
    extraRoutes: ['/e', '/time', '/daily-logs', '/forms'],
    items: [
      { to: '/schedule', icon: CalendarRange, label: 'Field Board', permission: 'schedule.view', keywords: 'schedule board calendar scheduling', description: "The crew schedule — who's on which job, and when" },
      { to: '/crews', icon: Users2, label: 'Crews', permission: 'schedule.view', keywords: 'roster gangs teams', description: 'Crew rosters and assignments' },
      { to: '/time/entries', icon: Timer, label: 'Time Entry', permission: 'timecards.view', keywords: 'time entry timecard timecards timesheet hours payroll approve posting foundation earn code overtime time log history trace identities crew week fill shift', description: 'Enter the crew’s time, approve it, and post it to Foundation' },
    ],
  },
  {
    key: 'equipment',
    label: 'Equipment',
    icon: Truck,
    items: [
      { to: '/equipment', icon: Truck, label: 'Equipment', tabLabel: 'Overview', permission: 'equipment.view', keywords: 'fleet machine attachment rental hire', description: 'Fleet, assignments, and inspections' },
      { to: '/equipment/fleet', icon: Radar, label: 'Live Fleet', tabLabel: 'Fleet', permission: 'equipment.view', keywords: 'samsara fleet map trackers gps live', description: 'Every tracked machine on one map, tracker health, billing check' },
      { to: '/equipment/inbox', icon: Inbox, label: 'Equipment Inbox', tabLabel: 'Inbox', permission: 'equipment.approve_moves', keywords: 'equipment inbox requests moves approve deny vic', badge: 'equipment_inbox', description: 'Equipment requests — send, hire, or deny' },
    ],
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    icon: ShoppingCart,
    items: [
      {
        to: '/purchasing', icon: ShoppingCart, label: 'Purchasing', tabLabel: 'Overview',
        keywords: 'purchase order request PO buy',
        description: 'Purchase requests and orders',
        // Every purchasing grant reaches the workspace: creators and their own
        // requests included, not only the office.
        show: (can) =>
          can('purchase-requests.create') ||
          can('purchase-requests.view-own') ||
          can('purchase-requests.view-project') ||
          can('purchase-requests.view_all') ||
          can('purchase-requests.manage'),
      },
      { to: '/purchasing/inbox', icon: Inbox, label: 'Purchasing Inbox', tabLabel: 'Inbox', permission: 'purchase-requests.manage', keywords: 'triage queue requests', badge: 'purchasing_inbox', description: 'Triage incoming purchase requests' },
      { to: '/invoices', icon: FileText, label: 'Invoices', keywords: 'bill AP payable approve', description: 'Your assigned invoices — create, assign, and approve' },
      // "PO Command Center" → Analytics (Michael: "Use plain names").
      { to: '/purchasing/analytics', icon: BarChart3, label: 'Purchasing Analytics', tabLabel: 'Analytics', permission: 'purchase-orders.view', keywords: 'commitments spend analytics po command center', description: 'Purchasing commitments, spend, and what needs attention' },
      // "Access vendor information through purchasing" — a tab here.
      { to: '/vendors', icon: Building, label: 'Vendors', permission: 'vendors.view', keywords: 'supplier subcontractor compliance', description: 'Vendors, subcontractors, and compliance docs' },
    ],
  },
  {
    key: 'people',
    label: 'People & Safety',
    icon: Users,
    items: [
      { to: '/employees', icon: Users, label: 'Employees', permission: 'employees.view', keywords: 'staff roster people hr', description: 'Directory and employee records' },
      { to: '/certifications', icon: BadgeCheck, label: 'Certifications', permission: 'employees.view', keywords: 'osha cards training expiring credentials qualifications', description: 'Who holds what, and what is expiring' },
      { to: '/safety', icon: HardHat, label: 'Safety', permission: 'safety.view', keywords: 'toolbox talk sds jha', description: 'Safety Command — risk, hazards, and field readiness' },
      { to: '/incidents', icon: TriangleAlert, label: 'Incidents', permission: 'incidents.view', keywords: 'injury near miss accident', description: 'Incident reports and near-misses' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: BarChart3,
    home: '/reports/overview',
    // ScrapTrack's own pages are report surfaces without a menu row of
    // their own.
    extraRoutes: ['/scraptrack'],
    items: [
      // The management dashboard moved here from "/" (Michael: "Move the
      // management dashboard into Reports").
      { to: '/reports/overview', icon: LayoutDashboard, label: 'Dashboard', tabLabel: 'Overview', keywords: 'dashboard overview management open jobs alerts', description: 'The management overview — open jobs, alerts, and the company at a glance' },
      { to: '/reports/job-costing', icon: Coins, label: 'Job Costing', permission: 'projects.view_financials', keywords: 'cost margin budget financials', description: 'Labor budget vs actual $, by active job' },
      // Equipment Usage (2026-09-29): for the people who oversee equipment
      // hours or post them — either grant, as the page and its API gate.
      {
        to: '/reports/equipment-usage', icon: Gauge, label: 'Equipment Usage',
        keywords: 'equipment usage equipment hours machine hours not posted posting post submitted unbilled not billed billed hours billing foundation missed income aging',
        description: 'Equipment hours by job and machine, and what has not been posted yet',
        show: (can) => can('equipment.view_usage_report') || can('equipment.approve_usage') || can('equipment.bill_usage'),
      },
      { to: '/scraptrack/summary', icon: Recycle, label: 'Scrap Summary', permission: 'scraptrack.view', keywords: 'scrap tonnage metal revenue', description: 'Scrap tickets, tonnage, and revenue' },
      { to: '/insights', icon: BarChart3, label: 'Insights', keywords: 'analytics charts kpi', description: 'KPIs, today’s schedule, compliance, and reports' },
      {
        to: '/reports', icon: ScrollText, label: 'All Reports', tabLabel: 'All reports',
        keywords: 'report costing',
        description: 'Every report in DemoOps, in one list',
        // One clause per row of REPORTS in ReportsPage.tsx, and nothing else:
        // a grant listed here without a row there offers a page that answers
        // "forbidden". invoices.audit left with the Invoice Final Report
        // (2026-09-29); Equipment Usage brought its two grants the same day.
        show: (can) => can('projects.view_financials')
          || can('equipment.view_usage_report') || can('equipment.approve_usage') || can('equipment.bill_usage')
          // Hours by Section (2026-10-19).
          || can('timecards.create') || can('timecards.create_all'),
      },
    ],
  },
  {
    // Tools: the utilities Michael wanted out of the page list (2026-09-08).
    // The desktop sidebar renders this group as its footer strip; the phone
    // sheet and /menu list it like any other. "Tools" is fine as a tooltip
    // and hopeless as a heading on /menu, whose own title is already "More".
    key: 'tools',
    label: 'Tools',
    headingLabel: 'Admin & Tools',
    icon: MoreHorizontal,
    placement: 'footer',
    // The assistant page is reached from its button, not a menu row.
    extraRoutes: ['/nadc-ai'],
    items: [
      {
        to: '/admin', icon: ShieldCheck, label: 'Admin',
        keywords: 'users roles permissions audit settings config',
        description: 'Queue health, failed jobs, and user roles',
        show: (can) => can('admin.system_config') || can('admin.view_audit_log') || can('users.assign_roles'),
      },
      { to: '/settings', icon: Settings, label: 'Settings', keywords: 'profile preferences sessions devices account', description: 'Your profile, access, sessions, and devices' },
      // Help is the in-app Guide, not a page: it opens the panel RootLayout
      // already mounts. `to` is a '#' id precisely because no pathname can
      // match it, so it can never be resolved as a route.
      { to: '#guide', action: 'guide', icon: LifeBuoy, label: 'Help', keywords: 'guide help support how do i stuck ticket', description: 'Open the Guide — how this screen works, and how to raise a ticket' },
      { to: '/whats-new', icon: Megaphone, label: "What's New", keywords: 'changelog updates release notes', description: 'Recent changes to DemoOps, in plain language' },
      // The portal owns help desk tickets; DemoOps has no ticket screen and
      // must not pretend to. `external` makes every surface render an <a>.
      { to: 'https://nadc1.net/my-tickets', external: true, icon: Ticket, label: 'My Tickets', keywords: 'helpdesk support ticket portal', description: 'Your help desk tickets, on the NADC portal' },
      // Preview site (2026-09-25): a branch's frontend on the production
      // backend, for looking at a change before it goes live. For everyone
      // since 2026-10-20 (Michael: "everyone should be able to see it") — the
      // site was always open to any DemoOps login; only this link was admin's.
      {
        to: 'https://preview.nadc1.net', external: true, icon: FlaskConical, label: 'Preview site',
        keywords: 'preview staging next version branch review before live',
        description: 'The next version of DemoOps, before it goes live',
        hint: '(opens the preview site)',
      },
      // `badge: 'unsent'` — how much of this device's work the server does
      // not have yet (offline reliability §2).
      { to: '/sync', icon: RefreshCw, label: 'Sync', keywords: 'offline queue sync center pending unsent', badge: 'unsent', description: 'Offline queue and sync status' },
    ],
  },
];

/**
 * Operator experience (2026-09-17, spec §2.3). A person whose /me says
 * `experience: 'operator'` — the Operator preset, or the switch on Admin →
 * Access → People — gets ONE workspace: My job, the page with their job(s)
 * and the three actions (hours, inspect, swap attachment). No footer, no
 * Settings, no Help: the sign-out row is the only other thing on screen.
 *
 * It is a SEPARATE configuration rather than a `show` gate on an entry in
 * NAV_WORKSPACES so that the full configuration — and every test that reads
 * it by hand — is untouched, and so that every surface reading the nav gets
 * the same answer from one place: navWorkspacesFor(experience). My job
 * carries no permission on purpose; the server scopes what it shows.
 */
export const OPERATOR_WORKSPACES: NavWorkspace[] = [
  {
    key: 'my-job',
    label: 'My job',
    icon: HardHat,
    items: [
      { to: '/my-job', icon: HardHat, label: 'My job', keywords: 'my job today machines hours inspect swap attachment', description: 'Your job today and the machines on it' },
    ],
  },
];

/** The experience value the nav filters on — AuthUser.experience, or nothing. */
export type NavExperience = 'full' | 'operator' | string | undefined;

/** THE configuration this person navigates: My job alone for an operator, everything otherwise. */
export function navWorkspacesFor(experience?: NavExperience): NavWorkspace[] {
  return experience === 'operator' ? OPERATOR_WORKSPACES : NAV_WORKSPACES;
}

/** Every destination in the (full) configuration, in workspace order. */
export function allNavItems(): NavItem[] {
  return NAV_WORKSPACES.flatMap((w) => w.items);
}

/** Every destination this person can reach, flattened — the palette's corpus. */
export function visibleNavItems(can: (p: string) => boolean, experience?: NavExperience): NavItem[] {
  return navWorkspacesFor(experience).flatMap((w) => w.items).filter((i) => navItemVisible(i, can));
}

/**
 * What the flyout and /menu print above a workspace's list. The strip keeps
 * `label` — an icon tooltip has no room for more.
 */
export function workspaceHeading(workspace: NavWorkspace): string {
  return workspace.headingLabel ?? workspace.label;
}

/** One workspace's items, minus the ones this person cannot reach. */
export function workspaceItems(workspace: NavWorkspace, can: (p: string) => boolean): NavItem[] {
  return workspace.items.filter((i) => navItemVisible(i, can));
}

/** The area's tab strip: its visible pages, in order (2026-09-25). */
export function workspaceTabs(workspace: NavWorkspace, can: (p: string) => boolean): NavItem[] {
  return workspaceItems(workspace, can).filter((i) => isRouteItem(i) && (i.placement ?? 'tab') === 'tab');
}

/** The area's actions — buttons on the strip's right (Job Blast under Projects). */
export function workspaceActions(workspace: NavWorkspace, can: (p: string) => boolean): NavItem[] {
  return workspaceItems(workspace, can).filter((i) => i.placement === 'action');
}

/**
 * Where the sidebar row for a workspace goes: its declared `home`, else the
 * first page this person can open. Null when nothing inside is theirs — the
 * row is not rendered then (visibleWorkspaces).
 */
export function workspaceHome(workspace: NavWorkspace, can: (p: string) => boolean): string | null {
  if (workspace.home) return workspace.home;
  return workspaceTabs(workspace, can)[0]?.to ?? null;
}

/**
 * The badge names a workspace's row sums (2026-09-25: "Put notification
 * counts on the parent item. 'Purchasing · 6' can signal pending work
 * without giving each inbox its own permanent menu link"). Only the pages
 * this person can open count — a badge for a page you cannot reach is noise.
 */
export function workspaceBadges(workspace: NavWorkspace, can: (p: string) => boolean): string[] {
  return workspaceItems(workspace, can).map((i) => i.badge).filter((b): b is string => !!b);
}

/**
 * The workspaces worth showing: one whose every item is permission-hidden is
 * not an empty section or strip icon, it is nothing at all.
 */
export function visibleWorkspaces(can: (p: string) => boolean, experience?: NavExperience): NavWorkspace[] {
  return navWorkspacesFor(experience).filter((w) => workspaceItems(w, can).length > 0);
}
/** The desktop sidebar's headed sections — every visible workspace not filed in the footer. */
export function mainWorkspaces(can: (p: string) => boolean, experience?: NavExperience): NavWorkspace[] {
  return visibleWorkspaces(can, experience).filter((w) => (w.placement ?? 'main') === 'main');
}
/** The desktop sidebar's footer strip. */
export function footerWorkspaces(can: (p: string) => boolean, experience?: NavExperience): NavWorkspace[] {
  return visibleWorkspaces(can, experience).filter((w) => w.placement === 'footer');
}

/**
 * Does `route` own `pathname`? Segment-aware, so /equipment never claims
 * /equipmentx, and "/" is an exact match only — otherwise the Dashboard would
 * own every page in the app.
 */
function routeOwns(route: string, pathname: string): boolean {
  if (route === '/') return pathname === '/';
  return pathname === route || pathname.startsWith(`${route}/`);
}

/**
 * Which workspace is the current page in?
 *
 * LONGEST PREFIX WINS, which is the whole reason this is one function rather
 * than a chain of startsWith guesses scattered through the layouts:
 * /reports/job-costing is Jobs' Job Costing and not Office's Reports, and
 * /purchasing/analytics is Office's PO Command Center and not Office's
 * Purchasing. Deep routes resolve to their parent — /projects/:id is Projects,
 * /safety/permits is Safety — so the strip never goes blank underneath someone.
 *
 * Permission-blind on purpose: it answers "where am I", not "may I". Callers
 * that render the answer filter it themselves.
 */
export function workspaceForPath(pathname: string, experience?: NavExperience): NavWorkspace | undefined {
  let best: { workspace: NavWorkspace; length: number } | undefined;

  for (const workspace of navWorkspacesFor(experience)) {
    const routes = [
      // Only in-app routes: My Tickets' `to` is a portal URL and Help's is a
      // '#' id, and neither is anywhere someone can be standing.
      ...workspace.items.filter(isRouteItem).map((i) => i.to),
      ...(workspace.extraRoutes ?? []),
    ];
    for (const route of routes) {
      if (!routeOwns(route, pathname)) continue;
      if (!best || route.length > best.length) best = { workspace, length: route.length };
    }
  }

  return best?.workspace;
}

/**
 * Which menu entry is the current page? Same longest-prefix rule, so the panel
 * marks Equipment Inbox rather than Equipment when you are standing in the
 * inbox, and still marks Projects while you are three levels inside a job.
 */
export function activeNavItem(pathname: string, experience?: NavExperience): NavItem | undefined {
  let best: { item: NavItem; length: number } | undefined;

  for (const item of navWorkspacesFor(experience).flatMap((w) => w.items)) {
    if (!isRouteItem(item)) continue;
    if (!routeOwns(item.to, pathname)) continue;
    if (!best || item.to.length > best.length) best = { item, length: item.to.length };
  }

  return best?.item;
}
